import { useQuery } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { showNotification } from "@mantine/notifications";
import { customAxios } from "lib/axios";

interface Activity {
  punchInTime: string;
  punchOutTime?: string;
  sessionStatus: string;
  workingHours: number;
}

interface DayAttendance {
  status: "present" | "absent" | "half" | string;
  workingHours: number;
  notes: string;
  recordId: string | null;
  activities: Activity[];
}

interface AttendanceRecord {
  employee: {
    id: string;
    name: string;
    email: string;
  };
  attendance: Record<string, DayAttendance>;
}

interface AttendanceResponse {
  success: boolean;
  data: {
    month?: string;
    year?: string;
    daysInMonth?: number;
    attendanceData: AttendanceRecord[];
  };
}

export interface AttendanceFilters {
  type?: "month" | "date" | "range" | string;
  month?: number;
  year?: number;
  date?: string;
  startDate?: string;
  endDate?: string;
}

const buildQueryString = (filters: AttendanceFilters) => {
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      params.append(key, String(value));
    }
  });
  const qs = params.toString();
  return qs ? `?${qs}` : "";
};

export const getAttendanceAdmin = async (
  filters: AttendanceFilters
): Promise<AttendanceResponse> => {
  const qs = buildQueryString(filters);
  const url = `/attendance/admin${qs}`;
  const { data } = await customAxios.get<AttendanceResponse>(url);
  return data;
};

export const useGetAttendanceAdminV2 = (filters: AttendanceFilters) =>
  useQuery<AttendanceResponse, AxiosError>(
    ["admin-attendance", JSON.stringify(filters)],
    () => getAttendanceAdmin(filters),
    {
      onError: (error) =>
        showNotification({
          title: "Error Loading Attendance Data",
          message:
            (error.response?.data as { message?: string })?.message ??
            "Failed to load attendance data",
          color: "red",
        }),
      staleTime: 0,
      retry: 1,
      refetchOnMount: "always",
    }
  );
